/**
 * What goes on a restaurant bill besides the food.
 *
 * A takeaway leaves in a box and a delivery leaves on a scooter, and both cost
 * the restaurant money that nothing was recovering. Each is a per-property
 * amount an owner sets, because what a hotel charges for packing is its own
 * decision, not something a billing system should invent.
 *
 * Service charge is here too, and it defaults to zero on purpose. Indian
 * consumer guidance is that it cannot be added automatically or by default to a
 * food bill, so it only ever appears where an owner has gone and set one.
 */
import { pctOf, roundHalfUp } from './money.js';
export const NO_CHARGES = {
    packagingTakeaway: 0,
    packagingDelivery: 0,
    deliveryFee: 0,
    serviceChargePct: 0,
};
/**
 * The charges this ticket carries.
 *
 * A charge set to zero produces no line at all rather than a line for nothing -
 * a bill listing "Packing charges ₹0.00" only invites the question.
 */
export function extraCharges(input) {
    const { orderType, net, settings } = input;
    const charges = [];
    if (orderType === 'TAKEAWAY' && settings.packagingTakeaway > 0) {
        charges.push({ key: 'PACKAGING', label: 'Packing charges', amount: settings.packagingTakeaway });
    }
    if (orderType === 'DELIVERY') {
        if (settings.packagingDelivery > 0) {
            charges.push({
                key: 'PACKAGING',
                label: 'Packing charges',
                amount: settings.packagingDelivery,
            });
        }
        // Charged even on a fully discounted order: it still cost somebody a
        // scooter and a bag.
        if (settings.deliveryFee > 0) {
            charges.push({ key: 'DELIVERY', label: 'Delivery charges', amount: settings.deliveryFee });
        }
    }
    if ((orderType === 'DINE_IN' || orderType === 'ROOM_SERVICE') &&
        settings.serviceChargePct > 0 &&
        net > 0) {
        charges.push({
            key: 'SERVICE',
            label: `Service charge (${settings.serviceChargePct}%)`,
            amount: pctOf(net, settings.serviceChargePct),
        });
    }
    return charges;
}
/**
 * The GST rate a charge is taxed at.
 *
 * Packing and delivery are not supplies of their own: they are part of the
 * composite supply of restaurant service, so they take the rate of the
 * principal supply - the food. Where a ticket carries more than one rate, the
 * one carrying the most value is the principal one.
 */
export function principalGstRate(lines, fallback = 5) {
    if (lines.length === 0)
        return fallback;
    const byRate = new Map();
    for (const line of lines) {
        byRate.set(line.gstRate, (byRate.get(line.gstRate) ?? 0) + line.taxable);
    }
    let best = lines[0].gstRate;
    let most = -1;
    for (const [rate, taxable] of byRate) {
        if (taxable > most) {
            most = taxable;
            best = rate;
        }
    }
    return best;
}
/**
 * A bill discount given as a percentage of what is left to pay.
 *
 * A percentage is how a counter actually thinks about it - "ten percent off" -
 * and it stays right when the ticket changes underneath, where a rupee amount
 * typed against a different total does not.
 */
export function billDiscountOf(net, percent) {
    if (!Number.isFinite(percent) || percent <= 0 || net <= 0)
        return 0;
    return Math.min(net, roundHalfUp((net * Math.min(percent, 100)) / 100));
}
//# sourceMappingURL=charges.js.map